import { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, HandCoins, CheckCircle2, CalendarClock, Download, FileText, AlertTriangle, Search } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { TablaAmortizacionView } from "@/components/creditos/TablaAmortizacionView";
import { FactorajeDesembolsoPanel } from "@/components/creditos/FactorajeDesembolsoPanel";
import { RemanentesPorDevolver } from "@/components/creditos/RemanentesPorDevolver";
import { esCreditoFactoraje, PERIODICIDAD_FACTORAJE } from "@/lib/factoraje";
import { esCreditoArrendamiento } from "@/lib/arrendamiento";
import { ArrendamientoDesembolsoPanel, ARR_FORM_VACIO, bloqueoArrendamiento, bodyArrendamiento } from "@/components/creditos/ArrendamientoDesembolsoPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { MEDIOS_DISPERSION, labelMedioPago } from "@/lib/catalogos_maestros";
import { AnclaPagoSelect, anclaLabel } from "@/components/common/AnclaPagoSelect";
import { PanelConfirmacionIVA } from "@/components/creditos/PanelConfirmacionIVA";
import { TooltipButton } from "@/components/common/Tooltips";
import { AcuseDesembolsoIntegranteButton } from "@/components/AcuseDesembolsoIntegranteButton";
import { CancelarCreditoButton } from "@/components/creditos/CancelarCreditoButton";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Checkbox } from "@/components/ui/checkbox";
import { MigracionCarteraPanel, MIG_VACIA, bodyMigracion, errorMigracion } from "@/components/creditos/MigracionCarteraPanel";

//: oct-2026 · Mínimo del motivo cuando la disposición se registra con fecha PASADA
//  (espejo de `desembolso.MIN_MOTIVO_RETRO`).
const MIN_MOTIVO_RETRO = 10;
//: oct-2026 (ajuste del PO) · Respaldo del tope de días hacia atrás si la ficha del backend
//  todavía no llegó (el valor que manda es `dias_retro_max`).
const DIAS_RETRO_MAX_DEFAULT = 20;
const restaDias = (iso, dias) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};
// El «hoy» del negocio es el de la Ciudad de México, igual que `today_str()` del backend.
// Con `toISOString()` (UTC) una captura de las 18:00 en México ya decía «mañana» y el
// backend la rechazaba por futura.
const hoyMX = () => new Intl.DateTimeFormat("en-CA",
  { timeZone: "America/Mexico_City", year: "numeric", month: "2-digit", day: "2-digit" })
  .format(new Date());

export default function MesaControl() {
  // Ejecutivo originador (interruptor de la entidad): solo «Desembolsos pendientes» propios.
  const { user } = useAuth();
  const esOriginador = user?.role === "ejecutivo_credito";
  const esAdmin = user?.role === "admin_entidad";
  // Cartera migrada de otro sistema (solo admin_entidad).
  const [mig, setMig] = useState(MIG_VACIA);
  const [migPreview, setMigPreview] = useState(null);
  const [migPreviewing, setMigPreviewing] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [realizados, setRealizados] = useState([]);
  const [realizadosTotal, setRealizadosTotal] = useState(0);
  // oct-2026 — buscador + paginación de "Desembolsos realizados" (bandeja independiente
  // de `pendientes`): texto de búsqueda, estado del botón "Ver más" y el timer del debounce.
  const [qRealizados, setQRealizados] = useState("");
  const [realizadosLoading, setRealizadosLoading] = useState(true);
  const [realizadosLoadingMore, setRealizadosLoadingMore] = useState(false);
  const debounceRealizadosRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ medio_dispersion: "", cuenta_destino: "", fecha_desembolso: "", referencia_dispersion: "", ancla_pago: "", motivo_retroactivo: "" });
  const [saving, setSaving] = useState(false);
  const [previewAncla, setPreviewAncla] = useState(null);
  const [fxPrev, setFxPrev] = useState(null);
  const [arrForm, setArrForm] = useState(ARR_FORM_VACIO);
  // Ventana de captura retroactiva + periodos ya reportados (los manda /desembolso/*).
  const [ventana, setVentana] = useState({});
  const hoy = hoyMX();
  // Disposición retroactiva: cualquier fecha anterior a hoy. Exige motivo, sin comprobante.
  const esRetroactivo = !!form.fecha_desembolso && form.fecha_desembolso < hoy;
  const esFx = esCreditoFactoraje(sel);
  // Build 2 · Arrendamiento: beneficiario = PROVEEDOR del bien (CLABE precargada, no editable).
  const esArr = esCreditoArrendamiento(sel);
  const arrDes = sel?.arrendamiento_desembolso;
  const arrBloqueo = esArr ? bloqueoArrendamiento(arrDes, arrForm) : "";
  const lineaMigrada = !!sel?.fecha_disposicion_original;
  const pideMotivoRetro = esRetroactivo && !mig.activo;
  // oct-2026 (ajuste del PO) · Ventana de captura: hasta DIAS_RETRO_MAX días atrás. El backend
  // manda `fecha_min_disposicion`; si la ficha aún no llegó, se calcula el mismo piso aquí.
  const diasRetroMax = ventana.dias_retro_max ?? DIAS_RETRO_MAX_DEFAULT;
  const fechaMin = ventana.fecha_min_disposicion || restaDias(hoy, diasRetroMax);
  const periodoReportado = (form.fecha_desembolso && esRetroactivo)
    ? (ventana.periodos_reportados || {})[form.fecha_desembolso.slice(0, 7)] || null
    : null;
  const otorgamientoPrevio = !!form.fecha_desembolso && !!sel?.fecha_contratacion
    && form.fecha_desembolso < sel.fecha_contratacion;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // "Desembolsos realizados" vive en su propia bandeja con buscador/paginación
      // (buscarRealizados, más abajo); aquí solo cargamos "pendientes" + la ventana.
      const pendResp = await api.get("/desembolso/pendientes");
      setPendientes(pendResp.data.pendientes || []);
      setVentana({
        dias_retro_max: pendResp.data.dias_retro_max,
        fecha_min_disposicion: pendResp.data.fecha_min_disposicion,
        periodos_reportados: pendResp.data.periodos_reportados || {},
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // oct-2026 — "Desembolsos realizados": busca por número de crédito o cliente (q), con
  // paginación server-side (skip/limit). `queryText` SIEMPRE se pasa explícito (nunca se lee
  // del estado dentro de la función) para que el debounce del input nunca quede con un valor
  // viejo. `reset=true` reemplaza la lista (nueva búsqueda); `reset=false` la agrega ("Ver más").
  const buscarRealizados = useCallback(async (queryText, { reset = true, skip = 0 } = {}) => {
    if (reset) setRealizadosLoading(true); else setRealizadosLoadingMore(true);
    try {
      const resp = await api.get("/desembolsos-realizados", {
        params: { q: (queryText || "").trim() || undefined, skip, limit: 50 },
      });
      const nuevos = resp.data.realizados || [];
      setRealizados((prev) => (reset ? nuevos : [...prev, ...nuevos]));
      setRealizadosTotal(resp.data.total || 0);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRealizadosLoading(false);
      setRealizadosLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (esOriginador) return undefined;
    buscarRealizados("");
    return () => { if (debounceRealizadosRef.current) clearTimeout(debounceRealizadosRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onQRealizadosChange = (e) => {
    const v = e.target.value;
    setQRealizados(v);
    if (debounceRealizadosRef.current) clearTimeout(debounceRealizadosRef.current);
    debounceRealizadosRef.current = setTimeout(() => buscarRealizados(v), 400);
  };

  const onQRealizadosKeyDown = (e) => {
    if (e.key === "Enter") {
      if (debounceRealizadosRef.current) clearTimeout(debounceRealizadosRef.current);
      buscarRealizados(qRealizados);
    }
  };

  const verMasRealizados = () => buscarRealizados(qRealizados, { reset: false, skip: realizados.length });

  // `realizados` ya viene ordenado por desembolsado_at DESC (server-side); se acumula con
  // "Ver más" (oct-2026), nunca se reordena ni se deduplica aquí.
  const realizadosView = realizados;

  const abrir = async (c) => {
    // Al abrir, cargamos el detalle completo del crédito (incluye tabla proyectada) desde
    // /desembolso/{id}. El listado /desembolso/pendientes no trae la tabla para ahorrar payload.
    let det = c;
    try {
      const { data } = await api.get(`/desembolso/${c.id}`);
      det = { ...c, ...data };
      setSel(det);
    } catch (e) {
      setSel(c);  // fallback: al menos la ficha básica
    }
    setForm({
      medio_dispersion: c.medio_dispersion_solicitado || "transferencia",
      cuenta_destino: c.cuenta_destino_solicitada || "",
      fecha_desembolso: hoyMX(),
      referencia_dispersion: "",
      ancla_pago: det.ancla_pago ? String(det.ancla_pago) : "",
      motivo_retroactivo: "",
      ...(det.fecha_disposicion_original ? { fecha_desembolso: det.fecha_disposicion_original } : {}),
      ...(det.arrendamiento_desembolso
        ? { medio_dispersion: "transferencia", cuenta_destino: det.arrendamiento_desembolso.beneficiario?.clabe || "" }
        : {}),
    });
    setArrForm(ARR_FORM_VACIO);
    setFxPrev(null);
    setMig({ ...MIG_VACIA, activo: !!det.fecha_disposicion_original,
      numero_anterior: det.migracion_linea?.numero_anterior || "" });
  };

  useEffect(() => { setMigPreview(null); }, [mig, form.fecha_desembolso, form.ancla_pago]);

  const toggleMigracion = (v) => {
    setMig((m) => ({ ...m, activo: !!v }));
    if (!v && form.fecha_desembolso < fechaMin) setForm((f) => ({ ...f, fecha_desembolso: hoy }));
  };

  const previsualizar = async () => {
    const err = errorMigracion(mig);
    if (err) { toast.error(err); return; }
    setMigPreviewing(true);
    try {
      const { data } = await api.post(`/desembolso/${sel.id}/vista-previa-migracion`,
        { ...form, ...bodyMigracion(mig, form.fecha_desembolso) });
      setMigPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMigPreviewing(false);
    }
  };

  // Previsualización del primer pago proporcional ANTES de confirmar: reusa /simulador con
  // las condiciones del crédito + fecha real + ancla elegida (editable en el modal).
  useEffect(() => {
    if (!sel || !form.ancla_pago || !form.fecha_desembolso) { setPreviewAncla(null); return; }
    const ai = sel.amort_input || {};
    let cancel = false;
    api.post("/simulador", {
      monto: ai.monto ?? sel.monto_original,
      plazo: ai.plazo ?? sel.plazo,
      periodicidad: ai.periodicidad ?? sel.periodicidad,
      tasa_ordinaria: ai.tasa ?? sel.tasa_ordinaria,
      comision: ai.comision ?? 0,
      comision_apertura_tipo: ai.comision_tipo || "porcentaje",
      iva: ai.iva ?? 16,
      tipo_amortizacion: ai.tipo_amort || sel.tipo_amortizacion || "francesa",
      fecha_inicio: form.fecha_desembolso,
      ancla_pago: form.ancla_pago,
      primer_pago_modalidad: ai.primer_pago_modalidad || "solo_interes",
    }).then(({ data }) => { if (!cancel) setPreviewAncla(data); })
      .catch(() => { if (!cancel) setPreviewAncla(null); });
    return () => { cancel = true; };
  }, [sel, form.ancla_pago, form.fecha_desembolso]);

  const descargarAcuse = async (creditoId) => {
    try {
      const r = await api.get(`/creditos/${creditoId}/acuse-desembolso-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const desembolsar = async () => {
    if (!sel) return;
    // Cuenta destino solo se exige en transferencia y cheque. En efectivo no aplica.
    if (form.medio_dispersion !== "efectivo" && !form.cuenta_destino.trim()) {
      toast.error("Captura la cuenta destino."); return;
    }
    // oct-2026 · Disposición con fecha PASADA: motivo obligatorio (≥10). El backend lo
    // vuelve a exigir; aquí solo se evita el viaje con el error ya sabido.
    if (mig.activo) {
      const err = errorMigracion(mig) || (!migPreview && "Revisa la vista previa del saldo antes de confirmar.");
      if (err) { toast.error(err); return; }
    }
    if (pideMotivoRetro && form.motivo_retroactivo.trim().length < MIN_MOTIVO_RETRO) {
      toast.error(`Escribe el motivo del registro retroactivo (mínimo ${MIN_MOTIVO_RETRO} caracteres).`);
      return;
    }
    setSaving(true);
    try {
      const base = mig.activo ? { ...form, ...bodyMigracion(mig, form.fecha_desembolso) } : form;
      const body = esArr ? { ...base, ...bodyArrendamiento(arrDes, arrForm) } : base;
      const { data } = await api.post(`/creditos/${sel.id}/desembolsar`, body);
      const cid = sel.id;
      if (data.cartera_migrada) {
        toast.info(`Cartera migrada: ${data.pagos_historicos} pago(s) histórico(s) registrados.`, { duration: 10000 });
      }
      toast.success(`Crédito ${data.numero_credito} desembolsado. Próximo pago: ${data.proximo_pago || "—"}.`, {
        action: { label: "Descargar acuse", onClick: () => descargarAcuse(cid) },
        duration: 10000,
      });
      if (data.retroactivo) {
        toast.info(`Registrado en el periodo ${data.periodo_contable}: tabla, intereses, IVA, `
          + `pólizas, información fiscal, CONDUSEF, cartera SIC y reportes PLD quedan en ese mes.`,
          { duration: 12000 });
      }
      if (data.periodo_reportado) {
        toast.warning(data.periodo_reportado.aviso, { duration: 16000 });
      }
      setSel(null);
      load();
      if (!esOriginador) buscarRealizados(qRealizados);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const totalMonto = pendientes.reduce((a, c) => a + (c.monto_ministrado ?? c.monto_original ?? 0), 0);

  // Desglose de la ministración: el NETO viene calculado del backend (monto_ministrado);
  // aquí solo se decide QUÉ mostrar según la forma de cobro de la comisión.
  const comisionTotal = Number(sel?.comision || 0) + Number(sel?.iva_comision || 0);
  const modoComision = sel?.comision_cobro_modo || "descontada";
  const esDescontada = modoComision === "descontada" && comisionTotal > 0;
  const montoMinistrar = sel?.monto_ministrado ?? sel?.monto_original;

  return (
    <div data-testid="mesa-control-page">
      <PageHeader
        title="Mesa de Control"
        subtitle="Desembolso y ministración — la entrega del dinero al cliente, segregada de la originación y la autorización del comité."
      />

      <div className="space-y-6 px-6 py-6 md:px-8">
        {esOriginador && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="mc-banner-originador">
            Por configuración de la entidad puedes desembolsar los créditos de clientes que tú originaste y que el Comité aprobó o condicionó.
            El acuse indicará que el desembolso lo hizo el ejecutivo originador.
          </div>
        )}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard testid="mc-stat-pendientes" label="Pendientes de desembolso" value={pendientes.length} icon="HandCoins" />
          <StatCard testid="mc-stat-monto" label="Monto por ministrar" value={mxn(totalMonto)} icon="Banknote" accent="blue" />
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="desembolso-bandeja">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
            </div>
          ) : pendientes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="desembolso-vacio">
              <CheckCircle2 className="mb-2 h-8 w-8 text-emerald-500" />
              <p className="text-sm">No hay créditos pendientes de desembolso.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Crédito</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Producto</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                  <TableHead>Medio propuesto</TableHead>
                  <TableHead>Cuenta propuesta</TableHead>
                  <TableHead>Contratado</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendientes.map((c) => (
                  <TableRow key={c.id} data-testid={`desembolso-row-${c.id}`}>
                    <TableCell className="font-medium">{c.numero_credito}</TableCell>
                    <TableCell>{c.cliente}</TableCell>
                    <TableCell>{c.producto}{c.modalidad === "Grupal" ? " · Grupal" : ""}</TableCell>
                    <TableCell className="text-right tabular-nums">{mxn(c.monto_original)}</TableCell>
                    <TableCell>{labelMedioPago(c.medio_dispersion_solicitado) || "—"}</TableCell>
                    <TableCell>{c.es_arrendamiento
                      ? <span data-testid={`desembolso-row-proveedor-${c.id}`}>Proveedor · {c.arrendamiento_proveedor || "—"}</span>
                      : c.cuenta_destino_solicitada || "—"}</TableCell>
                    <TableCell>{fdate(c.fecha_contratacion)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        <TooltipButton tooltip="Registra la entrega del dinero al cliente y activa el crédito"
                          size="sm" data-testid={`desembolsar-btn-${c.id}`} onClick={() => abrir(c)}>
                          <HandCoins className="mr-1.5 h-4 w-4" /> Desembolsar
                        </TooltipButton>
                        {/* sep-2026 · Cancelación ANTES del desembolso (solo admin_entidad, con
                            motivo obligatorio). Al desembolsar, el crédito sale de esta bandeja
                            y el botón desaparece con la fila. */}
                        <CancelarCreditoButton credito={c} onDone={load} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {!esOriginador && (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="desembolso-realizados-bandeja">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3">
            <FileText className="h-4 w-4 text-forest-600" />
            <h3 className="text-sm font-semibold text-slate-700">Desembolsos realizados</h3>
            <div className="relative ml-auto w-full max-w-xs sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="realizados-buscador-input"
                className="h-8 pl-8 text-sm"
                placeholder="Buscar por número de crédito o cliente"
                value={qRealizados}
                onChange={onQRealizadosChange}
                onKeyDown={onQRealizadosKeyDown}
              />
            </div>
          </div>
          <div className="flex items-center border-b border-slate-100 px-4 py-1.5">
            <span className="text-[11px] text-slate-400" data-testid="realizados-cap">
              Mostrando {realizadosView.length} de {realizadosTotal}
            </span>
          </div>
          {realizadosLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
            </div>
          ) : realizadosView.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="realizados-vacio">
              <FileText className="mb-2 h-8 w-8 text-slate-300" />
              <p className="text-sm">{qRealizados.trim()
                ? "Sin resultados para esa búsqueda."
                : "Aún no hay desembolsos realizados."}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Crédito</TableHead>
                  <TableHead className="text-right">Monto entregado</TableHead>
                  <TableHead>Medio</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acuse</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {realizadosView.map((c) => (
                  <TableRow key={c.id} data-testid={`realizado-row-${c.id}`}>
                    <TableCell>{c.cliente || "—"}</TableCell>
                    <TableCell className="font-medium">{c.numero_credito || c.id?.slice(0, 8)}</TableCell>
                    <TableCell className="text-right tabular-nums">{mxn(c.monto_ministrado)}</TableCell>
                    <TableCell>{labelMedioPago(c.medio_dispersion) || "—"}</TableCell>
                    <TableCell>{fdate(c.fecha_desembolso || c.fecha_disposicion)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end gap-1">
                        <TooltipButton tooltip="Descargar el acuse de desembolso (PDF)"
                          size="sm" variant="outline"
                          data-testid={`acuse-btn-${c.id}`} onClick={() => descargarAcuse(c.id)}>
                          <Download className="mr-1.5 h-4 w-4" /> PDF
                        </TooltipButton>
                        {/* Bloque 5 (feb-2026) — Acuse por integrante en grupales.
                            Solo aparece cuando `es_grupal=true` y hay integrantes; cada
                            botón descarga el PDF individual del integrante (Art. 11 LTOSF
                            desglosado por porción). El endpoint valida estatus y grupo. */}
                        {c.es_grupal && Array.isArray(c.integrantes) && c.integrantes.length > 0 && (
                          <div className="flex flex-wrap items-center justify-end gap-1"
                               data-testid={`realizado-integrantes-${c.id}`}>
                            {c.integrantes.map((it) => (
                              <AcuseDesembolsoIntegranteButton
                                key={it.cliente_id}
                                credito={c}
                                integrante={it}
                                variant="compact"
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!realizadosLoading && realizadosView.length > 0 && realizadosView.length < realizadosTotal && (
            <div className="flex justify-center border-t border-slate-100 py-3">
              <Button variant="outline" size="sm" data-testid="realizados-ver-mas-btn"
                disabled={realizadosLoadingMore} onClick={verMasRealizados}>
                {realizadosLoadingMore
                  ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Cargando…</>
                  : "Ver más"}
              </Button>
            </div>
          )}
        </div>
        )}
        {!esOriginador && <RemanentesPorDevolver />}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent data-testid="desembolso-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar desembolso — {sel?.numero_credito}</DialogTitle>
            <DialogDescription>
              Confirma el medio y la cuenta destino. La fecha de disposición y el plan de pagos se anclan a la fecha real de entrega.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Fase 8+ (feb-2026) — Banner de crédito con monto neto ≤ 0.
                Aparece cuando el desembolso previsto es negativo o cero (típicamente
                por comisión mal aplicada como % siendo monto fijo — caso Zinali).
                El backend YA rechaza intentar desembolsar con HTTP 400, pero el operador
                necesita ver la advertencia ANTES de intentar. Solo lectura, no persiste. */}
            {typeof montoMinistrar === "number" && montoMinistrar <= 0 && (
              <div className="rounded-lg border-2 border-rose-500 bg-rose-50 p-3 text-sm text-rose-900"
                   data-testid="mc-alerta-neto-negativo">
                <p className="mb-1 font-bold uppercase tracking-wide">
                  ⚠ Crédito requiere revisión contable
                </p>
                <p className="text-xs">
                  Monto a ministrar sería <b className="font-mono">{mxn(montoMinistrar)}</b>.
                  La comisión de apertura (<span className="font-mono">{mxn(sel?.comision)}</span>) + IVA
                  (<span className="font-mono">{mxn(sel?.iva_comision)}</span>) exceden el monto del crédito
                  (<span className="font-mono">{mxn(sel?.monto_original)}</span>). Causa probable: el producto
                  tiene comisión como monto fijo pero se aplicó como porcentaje. No se puede desembolsar hasta
                  corregir la configuración del producto o marcar el crédito para ajuste contable manual.
                </p>
              </div>
            )}
            <div className="rounded-md bg-slate-50 px-3 py-2 text-sm" data-testid="desembolso-resumen-monto">
              <div className="flex justify-between"><span className="text-slate-500">Cliente</span><span className="font-medium">{sel?.cliente}</span></div>
              {esFx ? (
                <div className="flex justify-between" data-testid="desembolso-fx-periodicidad"><span className="text-slate-500">Periodicidad</span><span>{PERIODICIDAD_FACTORAJE}</span></div>
              ) : sel?.plazo && sel?.periodicidad && (
                <div className="flex justify-between"><span className="text-slate-500">Plazo × periodicidad</span><span className="tabular-nums">{sel.plazo} pagos {String(sel.periodicidad).toLowerCase()}</span></div>
              )}
              {/* oct-2026 · Si el Comité recortó o estiró el plazo, queda a la vista contra
                  lo solicitado. La tabla definitiva se arma con el plazo autorizado. */}
              {Number(sel?.plazo_autorizado) > 0
                && Number(sel?.plazo_solicitado) > 0
                && Number(sel?.plazo_autorizado) !== Number(sel?.plazo_solicitado) && (
                <div className="flex justify-between" data-testid="desembolso-plazo-autorizado">
                  <span className="text-slate-500">Plazo solicitado / autorizado</span>
                  <span className="tabular-nums">
                    {sel.plazo_solicitado} → <b>{sel.plazo_autorizado} pagos</b> (Comité)
                  </span>
                </div>
              )}
              {esDescontada ? (
                <div className="mt-2 space-y-1 border-t border-slate-200 pt-2" data-testid="desembolso-desglose-descontada">
                  <div className="flex justify-between"><span className="text-slate-500">Monto del crédito</span><span className="tabular-nums">{mxn(sel?.monto_original)}</span></div>
                  <div className="flex justify-between text-rose-700"><span>− Comisión de apertura (descontada)</span><span className="tabular-nums">− {mxn(sel?.comision)}</span></div>
                  <div className="flex justify-between text-rose-700"><span>− IVA de la comisión</span><span className="tabular-nums">− {mxn(sel?.iva_comision)}</span></div>
                  <div className="mt-1.5 flex items-center justify-between rounded-md border-2 border-emerald-400 bg-emerald-50 px-3 py-2" data-testid="desembolso-monto-ministrar">
                    <span className="text-xs font-bold uppercase tracking-wide text-emerald-800">Monto a ministrar</span>
                    <span className="text-lg font-bold tabular-nums text-emerald-800">{mxn(montoMinistrar)}</span>
                  </div>
                  <p className="text-[11px] text-emerald-800">
                    Transfiere exactamente el monto a ministrar: la comisión de apertura y su IVA se descuentan del desembolso.
                  </p>
                </div>
              ) : (
                <div className="mt-2 space-y-1 border-t border-slate-200 pt-2">
                  <div className="flex items-center justify-between rounded-md border-2 border-emerald-400 bg-emerald-50 px-3 py-2" data-testid="desembolso-monto-ministrar">
                    <span className="text-xs font-bold uppercase tracking-wide text-emerald-800">{esArr ? "Monto a dispersar al proveedor" : "Monto a ministrar"}</span>
                    <span className="text-lg font-bold tabular-nums text-emerald-800">{mxn(montoMinistrar)}</span>
                  </div>
                  {esArr ? (
                    <p className="text-[11px] text-slate-500" data-testid="desembolso-nota-arrendamiento">
                      Valor del bien con IVA menos el enganche. {modoComision === "primera_cuota"
                        ? "La comisión de apertura se cobra en la primera renta."
                        : comisionTotal > 0 ? "La comisión de apertura se cobra por separado al cliente." : ""}
                    </p>
                  ) : comisionTotal > 0 && (
                    <p className="text-[11px] text-slate-500" data-testid="desembolso-nota-comision-cuotas">
                      La comisión de apertura ({mxn(sel?.comision)} + IVA {mxn(sel?.iva_comision)}) se cobra en las cuotas
                      {modoComision === "primera_cuota" ? " (primera cuota)" : " (diferida en el plazo)"}; no se descuenta del desembolso.
                    </p>
                  )}
                </div>
              )}
            </div>

            {esArr && <ArrendamientoDesembolsoPanel datos={arrDes} form={arrForm} setForm={setArrForm} />}

            {esFx && (
              <FactorajeDesembolsoPanel creditoId={sel.id} factoraje={sel.factoraje}
                fecha={form.fecha_desembolso} onPreview={setFxPrev} />
            )}

            {/* FASE B (feb-2026) — Desglose por integrante en crédito grupal.
                Muestra la porción sellada por Fase A: monto, comisión, IVA y neto
                a ministrar por miembro. Puramente informativo — el backend ya
                sella los campos por integrante en requisitos.py/desembolso.py.
                Solo aparece cuando el crédito es grupal y trae `integrantes[]`. */}
            {sel?.es_grupal && (sel?.integrantes || []).length > 0 && (
              <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3"
                   data-testid="desembolso-desglose-integrantes">
                <p className="mb-2 text-xs font-bold uppercase tracking-wide text-indigo-800">
                  Desglose por integrante ({sel.integrantes.length}) — grupal
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-slate-600">
                      <tr className="border-b border-indigo-200">
                        <th className="py-1 text-left">Rol</th>
                        <th className="py-1 text-left">Integrante</th>
                        <th className="py-1 text-right">Monto</th>
                        <th className="py-1 text-right">Comisión</th>
                        <th className="py-1 text-right">IVA</th>
                        <th className="py-1 text-right">Neto a ministrar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sel.integrantes.map((it) => {
                        const esTitular = it.rol === "titular";
                        const tieneAjuste = Math.abs(Number(it.ajuste_centavos_comision || 0)) > 0.001
                                              || Math.abs(Number(it.ajuste_centavos_iva || 0)) > 0.001;
                        return (
                          <tr key={it.cliente_id}
                              data-testid={`mc-integrante-row-${it.cliente_id}`}
                              className="border-b border-indigo-100">
                            <td className="py-1.5">
                              {esTitular
                                ? <span className="inline-block rounded bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold text-white">titular</span>
                                : <span className="text-slate-500">integrante</span>}
                            </td>
                            <td className="py-1.5">{it.cliente || it.nombre || it.cliente_id?.slice(0,8)}</td>
                            <td className="py-1.5 text-right font-mono tabular-nums"
                                data-testid={`mc-integrante-monto-${it.cliente_id}`}>
                              {mxn(it.monto)}
                            </td>
                            <td className="py-1.5 text-right font-mono tabular-nums"
                                data-testid={`mc-integrante-com-${it.cliente_id}`}>
                              {mxn(it.comision_integrante)}
                              {tieneAjuste && esTitular && (
                                <span className="ml-1 text-[10px] text-amber-700" title={`Ajuste de centavos: ${Number(it.ajuste_centavos_comision || 0).toFixed(2)} en comisión, ${Number(it.ajuste_centavos_iva || 0).toFixed(2)} en IVA`}>
                                  ±¢
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 text-right font-mono tabular-nums"
                                data-testid={`mc-integrante-iva-${it.cliente_id}`}>
                              {mxn(it.iva_comision_integrante)}
                            </td>
                            <td className="py-1.5 text-right font-mono font-semibold tabular-nums text-emerald-800"
                                data-testid={`mc-integrante-neto-${it.cliente_id}`}>
                              {mxn(it.monto_ministrado_integrante)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="font-semibold text-indigo-900">
                        <td colSpan="2" className="pt-1.5 text-left">Suma</td>
                        <td className="pt-1.5 text-right font-mono tabular-nums">
                          {mxn((sel.integrantes || []).reduce((a, it) => a + Number(it.monto || 0), 0))}
                        </td>
                        <td className="pt-1.5 text-right font-mono tabular-nums">
                          {mxn((sel.integrantes || []).reduce((a, it) => a + Number(it.comision_integrante || 0), 0))}
                        </td>
                        <td className="pt-1.5 text-right font-mono tabular-nums">
                          {mxn((sel.integrantes || []).reduce((a, it) => a + Number(it.iva_comision_integrante || 0), 0))}
                        </td>
                        <td className="pt-1.5 text-right font-mono tabular-nums">
                          {mxn((sel.integrantes || []).reduce((a, it) => a + Number(it.monto_ministrado_integrante || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="mt-1.5 text-[11px] text-indigo-700">
                  Cada integrante recibe su porción y su CFDI se timbra por separado en la bandeja de facturación (CFDI SOFOM · Desembolsos por facturar).
                </p>
              </div>
            )}

            {/* Proyección — el operador ve la tabla que se aplicará antes de sellar */}
            {sel?.tabla_amortizacion?.length > 0 && (
              <div data-testid="mc-proyeccion-tabla">
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
                  Plan de pagos previsto
                </p>
                <TablaAmortizacionView credito={esFx && fxPrev
                  ? { ...sel, tabla_amortizacion: fxPrev.tabla_amortizacion,
                      factoraje: { ...sel.factoraje, dias_descuento: fxPrev.dias_descuento } }
                  : sel} />
              </div>
            )}

            {/* CFDI Fase A: confirmación de IVA sobre intereses (Art. 15-X-b LIVA).
                El sistema sugiere; el operador confirma o corrige antes del desembolso. */}
            {sel?.id && <PanelConfirmacionIVA creditoId={sel.id} />}

            <div>
              <Label>Medio de dispersión</Label>
              <Select value={form.medio_dispersion} disabled={esArr} onValueChange={(v) => setForm((f) => ({
                ...f,
                medio_dispersion: v,
                // Al cambiar a efectivo, limpiamos la cuenta destino para que no arrastre
                // la CLABE fósil del contrato (medio original ≠ efectivo).
                cuenta_destino: v === "efectivo" ? "" : f.cuenta_destino,
              }))}>
                <SelectTrigger data-testid="desembolso-medio"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {MEDIOS_DISPERSION.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {sel?.medio_dispersion_solicitado && form.medio_dispersion
                && sel.medio_dispersion_solicitado !== form.medio_dispersion && (
                <p data-testid="desembolso-aviso-medio-difiere" className="mt-1 text-[11px] text-amber-700">
                  El crédito se contrató para dispersión por <b>{labelMedioPago(sel.medio_dispersion_solicitado)}</b>.
                  Estás desembolsando por <b>{labelMedioPago(form.medio_dispersion)}</b>.
                </p>
              )}
            </div>
            {form.medio_dispersion !== "efectivo" && (
              <div>
                <Label>{esArr ? "Cuenta destino (CLABE del proveedor)" : "Cuenta destino"}</Label>
                <Input data-testid="desembolso-cuenta" value={form.cuenta_destino} readOnly={esArr}
                       onChange={(e) => setForm((f) => ({ ...f, cuenta_destino: e.target.value }))}
                       placeholder="CLABE / cuenta / cheque" />
              </div>
            )}
            {esAdmin && (
              <label className="flex cursor-pointer items-start gap-2 rounded-md border border-indigo-300 bg-white px-3 py-2"
                data-testid="migracion-check-row">
                <Checkbox checked={mig.activo} onCheckedChange={toggleMigracion} disabled={lineaMigrada}
                  data-testid="migracion-check" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-semibold text-indigo-950">Crédito migrado de otro sistema</span>
                  <span className="block text-[11px] text-slate-600">
                    Fecha original hasta {sel?.anios_max_migracion ?? 10} años atrás y, en el mismo paso, las cuotas
                    que ya se pagaron en el sistema anterior.
                    {lineaMigrada && " Esta disposición se capturó como línea migrada: se desembolsa con su fecha original."}
                  </span>
                </span>
              </label>
            )}
            {lineaMigrada && !esAdmin && (
              <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="migracion-solo-admin">
                Esta disposición es cartera migrada de otro sistema: solo el Administrador de la entidad puede desembolsarla.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{mig.activo ? "Fecha original de disposición" : "Fecha de disposición"}</Label>
                {/* oct-2026 (ajuste del PO) · El piso ya NO es la contratación del sistema:
                    se permiten hasta 20 días atrás. Hay créditos otorgados en septiembre que
                    se capturaron en octubre y con el piso anterior solo salía «hoy». */}
                <Input type="date" data-testid="desembolso-fecha" value={form.fecha_desembolso}
                       min={mig.activo ? sel?.fecha_min_migracion : fechaMin} max={hoy} disabled={lineaMigrada}
                       onChange={(e) => setForm((f) => ({ ...f, fecha_desembolso: e.target.value }))} />
                <p className="mt-1 text-[11px] text-slate-500" data-testid="desembolso-fecha-ayuda">
                  {mig.activo
                    ? `Fecha en que se otorgó en el sistema anterior (desde ${fdate(sel?.fecha_min_migracion)}).`
                    : `Puedes registrar hasta ${diasRetroMax} días atrás. Todo se registra en el mes de la fecha elegida.`}
                </p>
              </div>
              <div>
                <Label>Referencia / folio</Label>
                <Input data-testid="desembolso-referencia" value={form.referencia_dispersion}
                       onChange={(e) => setForm((f) => ({ ...f, referencia_dispersion: e.target.value }))}
                       placeholder={form.medio_dispersion === "efectivo" ? "Folio de recibo de caja" : "Folio SPEI / cheque"} />
              </div>
            </div>
            {/* Punto 5 · El periodo de la fecha elegida ya tiene reportes generados o enviados:
                se avisa y NO se bloquea. */}
            {periodoReportado && (
              <div className="rounded-md border border-orange-300 bg-orange-50 p-3"
                   data-testid="desembolso-periodo-reportado">
                <p className="flex items-start gap-2 text-xs font-semibold text-orange-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  Este desembolso afecta un periodo ya reportado: revisa si requiere reporte
                  complementario.
                </p>
                <ul className="mt-1.5 space-y-0.5 pl-6 text-[11px] text-orange-800">
                  {(periodoReportado.reportes || []).slice(0, 6).map((r, i) => (
                    <li key={`rep-${i}`} data-testid={`desembolso-reporte-${i}`}>
                      {r.ambito} · {r.tipo} — {r.estatus}
                      {r.fecha_envio ? ` (enviado el ${fdate(r.fecha_envio)})` : ""}
                      {r.acuse ? ` · acuse ${r.acuse}` : ""}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 pl-6 text-[11px] text-orange-800">
                  Periodo {periodoReportado.periodo}: {periodoReportado.total} reporte(s),{" "}
                  {periodoReportado.enviados} ya enviado(s). El desembolso <b>sí</b> se registra.
                </p>
              </div>
            )}
            {/* Punto 2 · La fecha elegida es anterior a la contratación registrada: se guarda
                como fecha real de otorgamiento y la carátula y la tabla PDF la muestran. */}
            {otorgamientoPrevio && (
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3"
                   data-testid="desembolso-otorgamiento-previo">
                <p className="text-[11px] text-sky-900">
                  La fecha elegida ({fdate(form.fecha_desembolso)}) es anterior a la contratación
                  registrada en el sistema ({fdate(sel?.fecha_contratacion)}): se guardará también
                  como <b>fecha real de otorgamiento</b> y así la mostrarán la carátula del crédito
                  y la tabla de amortización en PDF.
                </p>
              </div>
            )}
            {mig.activo && (
              <MigracionCarteraPanel sel={sel} mig={mig} setMig={setMig} fecha={form.fecha_desembolso} hoy={hoy}
                preview={migPreview} previewing={migPreviewing} onPreview={previsualizar} />
            )}
            {pideMotivoRetro && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3"
                   data-testid="desembolso-retro-panel">
                <Label className="text-amber-900">
                  Motivo del registro retroactivo <span className="text-red-600">*</span>
                </Label>
                <Textarea data-testid="desembolso-motivo-retro" rows={2}
                  value={form.motivo_retroactivo}
                  onChange={(e) => setForm((f) => ({ ...f, motivo_retroactivo: e.target.value }))}
                  placeholder="Ej. el dinero se entregó en ventanilla el 15 de septiembre y el desembolso no se capturó en el sistema" />
                <p className="mt-1 text-[11px] text-amber-800">
                  Obligatorio, mínimo {MIN_MOTIVO_RETRO} caracteres. <b>No se pide comprobante.</b>{" "}
                  La bitácora guarda la fecha de disposición ({fdate(form.fecha_desembolso)}), la
                  fecha y hora reales de esta captura, tu usuario y este motivo.
                </p>
              </div>
            )}
            {!esFx && !esArr && (
            <div>
              <Label>Ancla de pago (opcional)</Label>
              <AnclaPagoSelect periodicidad={sel?.periodicidad} value={form.ancla_pago}
                onChange={(v) => setForm((f) => ({ ...f, ancla_pago: v }))}
                testid="desembolso-ancla" />
              <p className="mt-1 text-[11px] text-slate-500">
                Con ancla, todos los vencimientos caen en la fecha fija elegida y el primer pago
                cubre solo el proporcional de los días entre el desembolso y la primera ancla.
              </p>
            </div>
            )}
            {form.ancla_pago && previewAncla?.dias_proporcional != null && (
              <div data-testid="desembolso-preview-proporcional"
                className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-semibold">
                    Primer pago proporcional por {previewAncla.dias_proporcional} días — vence el {fdate(previewAncla.fecha_primer_pago)}.
                  </p>
                  <p>
                    {anclaLabel(sel?.periodicidad, form.ancla_pago)} · interés proporcional {mxn(previewAncla.interes_proporcional)}
                    {previewAncla.iva_interes_proporcional ? ` + IVA ${mxn(previewAncla.iva_interes_proporcional)}` : ""}
                    {previewAncla.primer_pago_modalidad === "cuota_completa"
                      ? " incluido en la primera cuota completa."
                      : " (el capital inicia en la siguiente cuota)."}
                  </p>
                </div>
              </div>
            )}
            {arrBloqueo && (
              <p className="flex items-start gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
                data-testid="desembolso-arr-bloqueo">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {arrBloqueo}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSel(null)} disabled={saving}>Cancelar</Button>
            <Button data-testid="desembolso-confirmar" onClick={desembolsar}
              disabled={saving || !!arrBloqueo || (lineaMigrada && !mig.activo) || (mig.activo && !migPreview) || (pideMotivoRetro
                && form.motivo_retroactivo.trim().length < MIN_MOTIVO_RETRO)}>
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
              Confirmar desembolso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
